import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Camera, CheckCircle2, Circle, Crown, ImagePlus, Loader2, Upload, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Btn, Page } from "@/components/kit";
import CameraCapture from "@/components/CameraCapture";
import { useScanDraft } from "@/context/auth";
import { usePaymentConfig, useScanUsage } from "@/hooks/queries";
import { guestProcessScan, processScan } from "@/lib/api";
import { markGuestUsed, setGuest } from "@/lib/guest";
import { isImageFile, periodLabel, prepareImage, rupees, toJpeg } from "@/lib/toto";
import { toast } from "sonner";

const STEPS = ["Reading card", "Identifying contact details", "Organizing information"];

function Slot({ label, file, onFile, onClear, testid }) {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
    if (!file) return setUrl(null);
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const take = async (f) => {
    if (!f) return;
    if (!isImageFile(f)) return toast.error("Please choose an image file (JPG, PNG or HEIC).");
    setBusy(true);
    try { onFile(await prepareImage(f)); }
    catch { onFile(f); }
    finally { setBusy(false); }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files?.[0]); }}
      className={`relative rounded-[20px] border-2 border-dashed transition-colors aspect-[1.7/1] overflow-hidden ${drag ? "border-[#0000EC] bg-[#E5E5FD]" : "border-[#CBD5E1] bg-[#F8FAFC]"}`} data-testid={testid}>
      {busy ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[14px] text-[#475569]" data-testid={`${testid}-preparing`}><Loader2 className="w-7 h-7 animate-spin text-[#0000EC]" />Preparing image…</div>
      ) : url ? (
        <>
          {broken ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[#F1F5F9] px-4 text-center" data-testid={`${testid}-no-preview`}>
              <ImagePlus className="w-7 h-7 text-[#64748B]" /><span className="text-[14px] font-semibold">Image added</span><span className="text-[12px] text-[#64748B] truncate max-w-full">{file?.name} · preview not supported by this browser</span>
            </div>
          ) : <img src={url} alt={label} onError={() => setBroken(true)} className="w-full h-full object-contain bg-[#0F172A]/5" data-testid={`${testid}-preview`} />}
          <button className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow" onClick={onClear} data-testid={`${testid}-clear`}><X className="w-5 h-5" /></button>
          <span className="absolute top-2 left-2 text-[12px] font-bold bg-white/90 rounded-full px-2.5 py-1">{label.replace(" (optional)", "")}</span>
        </>
      ) : (
        <button className="w-full h-full flex flex-col items-center justify-center gap-2 text-center px-4" onClick={() => input.current.click()} data-testid={`${testid}-pick`}>
          <span className="w-12 h-12 rounded-2xl bg-[#E5E5FD] flex items-center justify-center"><ImagePlus className="w-6 h-6 text-[#0000EC]" /></span>
          <span className="text-[15px] font-bold">{label}</span>
          <span className="text-[13px] text-[#64748B]">Tap to choose from gallery, or drag & drop (JPG, PNG, HEIC)</span>
        </button>
      )}
      <input ref={input} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { take(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
    </div>
  );
}

function Processing({ step }) {
  return (
    <div className="max-w-md mx-auto py-16 flex flex-col items-center" data-testid="scan-processing">
      <Loader2 className="w-10 h-10 animate-spin text-[#0000EC]" />
      <h2 className="mt-5 text-[22px] font-extrabold">Reading your card…</h2>
      <ul className="mt-6 space-y-3 w-full">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-3 text-[15px]">
            {i < step ? <CheckCircle2 className="w-5 h-5 text-[#059669]" /> : i === step ? <Loader2 className="w-5 h-5 animate-spin text-[#0000EC]" /> : <Circle className="w-5 h-5 text-[#E2E8F0]" />}
            <span className={i <= step ? "font-semibold" : "text-[#64748B]"}>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Scan({ guest }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { setDraft } = useScanDraft();
  const usage = useScanUsage().data;
  const pay = usePaymentConfig().data;
  const [mode, setMode] = useState("camera");
  const [camFailed, setCamFailed] = useState(false);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [phase, setPhase] = useState("pick");
  const [step, setStep] = useState(0);
  const [msg, setMsg] = useState("");
  const remaining = usage?.isPremium ? null : usage?.remainingScans;

  const onCamFail = useCallback((m) => { setCamFailed(true); setMode("upload"); toast.error(m); }, []);
  const onCapture = (f) => (front ? setBack(f) : setFront(f));
  const run = async (force) => {
    setPhase("processing");
    setStep(0);
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 800);
    try {
      if (guest) {
        const r = await guestProcessScan(await toJpeg(front), force);
        const thumb = await toJpeg(front, 700, 0.7).then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(b); }));
        markGuestUsed();
        try { setGuest({ ...r, thumb }); } catch { setGuest(r); }
        setDraft({ ...r, thumb, guest: true, localFront: front });
        return nav("/try/review");
      }
      const [fj, bj] = await Promise.all([toJpeg(front), back ? toJpeg(back) : null]);
      const r = await processScan(fj, bj, force);
      setDraft({ ...r, localFront: front, localBack: back });
      qc.invalidateQueries({ queryKey: ["scanUsage"] });
      nav("/scan/review");
    } catch (e) {
      setMsg(e.message || "We couldn't read this visiting card.");
      setPhase(e.status === 422 ? "quality" : e.status === 402 ? "limit" : "error");
    } finally {
      clearInterval(t);
    }
  };

  if (phase === "processing") return <Page><Processing step={step} /></Page>;
  if (phase !== "pick")
    return (
      <Page>
        <div className="max-w-md mx-auto py-16 flex flex-col items-center text-center gap-4" data-testid={`scan-${phase}`}>
          {phase === "limit" ? <Crown className="w-12 h-12 text-[#7C3AED]" /> : <AlertCircle className={`w-12 h-12 ${phase === "quality" ? "text-[#B54708]" : "text-[#DC2626]"}`} />}
          <h2 className="text-[22px] font-extrabold">{phase === "limit" ? "Free scans used up" : phase === "quality" ? "Image may be unclear" : "Couldn't read the card"}</h2>
          <p className="text-[15px] text-[#475569]" data-testid="scan-message">{msg}</p>
          <div className="flex flex-col gap-2 w-full mt-2">
            {phase === "limit" ? <Link to="/subscription" className="t-btn t-btn-primary" data-testid="scan-upgrade">{pay ? `Upgrade for ${rupees(pay.amount)}/${periodLabel(pay.durationDays)}` : "Upgrade to Premium"}</Link>
              : <Btn onClick={() => run(phase === "quality")} testid="scan-retry">{phase === "quality" ? "Use this image anyway" : "Try again"}</Btn>}
            <Btn variant="ghost" onClick={() => setPhase("pick")} testid="scan-retake">Choose another image</Btn>
          </div>
        </div>
      </Page>
    );

  return (
    <Page className="pt-4 lg:pt-8">
      <div className="flex items-end justify-between gap-4">
        <div><h1 className="text-[30px] lg:text-4xl font-extrabold">Scan a Card</h1><p className="text-[15px] text-[#475569]">AI reads the card and fills in the contact for you.</p></div>
        <span className="shrink-0 rounded-full bg-[#E5E5FD] text-[#0000EC] text-[13px] font-bold px-3 py-1.5" data-testid="scan-remaining">{guest ? "Guest · 1 free scan" : usage?.isPremium ? "Premium · Unlimited" : remaining != null ? `${remaining} free scans left` : "…"}</span>
      </div>
      <div className="grid grid-cols-2 p-1 rounded-[14px] bg-[#F1F5F9] mt-6 max-w-sm">
        {[["camera", "Camera", Camera], ["upload", "Upload", Upload]].map(([k, l, I]) => (
          <button key={k} onClick={() => { if (k === "camera") setCamFailed(false); setMode(k); }} data-testid={`scan-mode-${k}`} className={`h-10 rounded-[11px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors ${mode === k ? "bg-white text-[#0000EC] shadow-sm" : "text-[#475569]"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        {mode === "camera" && (
          <div>
            <CameraCapture onCapture={onCapture} autoStart onFail={onCamFail} />
            <p className="mt-2 text-[13px] text-[#64748B]">{front ? (back ? "Both sides captured." : "Front captured. Capture the back (optional).") : "Capture the front of the card."}</p>
          </div>
        )}
        {mode === "upload" && camFailed && <p className="lg:col-span-2 text-[14px] text-[#B54708] bg-[#FFFAEB] rounded-[12px] px-4 py-3" data-testid="scan-camera-fallback">Camera isn't available, so you can upload a photo of the card instead.</p>}
        <div className={`grid gap-4 ${mode === "upload" ? "sm:grid-cols-2 lg:col-span-2" : ""}`}>
          <Slot label="Front of card" file={front} onFile={setFront} onClear={() => setFront(null)} testid="scan-front" />
          {!guest && <Slot label="Back of card (optional)" file={back} onFile={setBack} onClear={() => setBack(null)} testid="scan-back" />}
        </div>
      </div>
      <Btn className="w-full sm:w-auto sm:min-w-[260px] mt-6" disabled={!front} onClick={() => run(false)} testid="scan-submit">Extract Details</Btn>
    </Page>
  );
}
